import React, { useState, useEffect } from 'react';
import { getRequests, TestRequest, updateRequestStatus } from '../../services/requestService';
import { getActiveTestPackageById, TestPackage } from '../../services/testPackageService';
import { getActiveInsuranceById, Insurance } from '../../services/insuranceService';
import { getActiveSupplementaryInsuranceById, SupplementaryInsurance } from '../../services/supplementaryInsuranceService';
import { toast } from 'react-toastify';
import API_CONFIG from '../../config/api';

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  const maxPageItems = 5;
  const pages = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxPageItems / 2));
  let endPage = Math.min(totalPages, startPage + maxPageItems - 1);

  if (endPage - startPage + 1 < maxPageItems) {
    startPage = Math.max(1, endPage - maxPageItems + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex justify-center mt-6">
      <nav className="flex items-center">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`mx-1 px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          قبلی
        </button>

        {startPage > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              1
            </button>
            {startPage > 2 && <span className="mx-1">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`mx-1 px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="mx-1">...</span>}
            <button onClick={() => onPageChange(totalPages)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`mx-1 px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          بعدی
        </button>
      </nav>
    </div>
  );
};

// Transaction interface
interface Transaction {
  _id: string;
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed';
  gateway: string;
  refId?: string;
  paymentDate?: string;
  createdAt: string;
  transactionType: 'request' | 'remaining';
}

// Interface for detailed test request
interface DetailedTestRequest extends TestRequest {
  patientInfo?: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation?: {
    type: 'home' | 'lab';
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo?: {
    region: string;
    field: string;
    street: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
    coordinates?: [number, number];
  };
  ageInfo?: {
    age: number;
  };
  insuranceInfo?: {
    type: string;
    nationalIdImageUrl?: string;
    testListImageUrl?: string;
    prescriptionImageUrl?: string;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
  };
  amount?: number; // Amount to be paid (in Toman)
  transportationPrice?: number; // Transportation price (هزینه ایاب و ذهاب)
  paymentExtensionRequest?: boolean; // Payment extension request
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected'; // Payment extension status
  paymentExtensionRejectionReason?: string; // Payment extension rejection reason
  samplingDate?: string; // Sampling date
  samplingTime?: string; // Sampling time
  statusLogs?: Array<{
    timestamp: string;
    changedBy: {
      userId: string;
      userName: string;
      userType: 'admin' | 'user';
    };
    action: string;
    oldStatus?: string;
    newStatus?: string;
    details?: string;
  }>;
}

// Helper functions
const translateStatus = (status: string, request?: TestRequest): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;
      
      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }
  
  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    canceled: 'لغو شده',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده',
  };
  return statusMap[status] || status;
};

const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    parent: 'والدین',
    other: 'سایر'
  };
  return typeMap[type] || type;
};

const getInsuranceTypeDisplay = (type: string) => {
  switch (type) {
    case 'free': return 'آزاد';
    case 'paper': return 'دفترچه بیمه';
    case 'electronic': return 'نسخه الکترونیک';
    case 'package': return 'پکیج آزمایشگاه';
    default: return type;
  }
};

const getBasicInsuranceDisplay = (insuranceId: string | undefined, selectedInsurance: Insurance | null) => {
  if (!insuranceId) return '';
  if (insuranceId === 'none') return 'هیچکدام';
  
  if (selectedInsurance && selectedInsurance._id === insuranceId) {
    return selectedInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

const getSupplementaryInsuranceDisplay = (supplementaryInsurance: string | undefined, selectedSupplementaryInsurance: SupplementaryInsurance | null) => {
  if (!supplementaryInsurance) return '';
  if (supplementaryInsurance === 'none') return 'هیچکدام';
  
  if (selectedSupplementaryInsurance && selectedSupplementaryInsurance._id === supplementaryInsurance) {
    return selectedSupplementaryInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

// RequestDetails component
interface RequestDetailsProps {
  requestId: string | null;
  onClose: () => void;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ requestId, onClose }) => {
  const [requestDetails, setRequestDetails] = useState<DetailedTestRequest | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TestPackage | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(null);
  const [selectedSupplementaryInsurance, setSelectedSupplementaryInsurance] = useState<SupplementaryInsurance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);

  // Fetch transactions for this test request
  const fetchTransactions = async (testRequestId: string, token: string) => {
    try {
      setLoadingTransactions(true);
      const response = await fetch(`${API_CONFIG.baseURL}/payments/test-request/${testRequestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTransactions(data.data || []);
        }
      }
    } catch (error) {

    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (!requestId) return;

    const fetchRequestDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('خطا در دریافت اطلاعات درخواست');
        }

        const data = await response.json();
        setRequestDetails(data.data);
        
        // Fetch transactions for this request
        fetchTransactions(requestId, token);
        
        // Fetch package details if available
        if (data.data?.insuranceInfo?.selectedPackage) {
          try {
            const packageDetails = await getActiveTestPackageById(data.data.insuranceInfo.selectedPackage);
            setSelectedPackage(packageDetails);
          } catch (packageError) {

          }
        }
        
        // Fetch insurance details if available
        if (data.data?.insuranceInfo?.basicInsurance) {
          try {
            const insuranceDetails = await getActiveInsuranceById(data.data.insuranceInfo.basicInsurance);
            setSelectedInsurance(insuranceDetails);
          } catch (insuranceError) {

          }
        }

        // Fetch supplementary insurance details if available
        if (data.data?.insuranceInfo?.supplementaryInsurance && data.data.insuranceInfo.supplementaryInsurance !== 'none') {
          try {
            const supplementaryInsuranceDetails = await getActiveSupplementaryInsuranceById(data.data.insuranceInfo.supplementaryInsurance);
            setSelectedSupplementaryInsurance(supplementaryInsuranceDetails);
          } catch (supplementaryInsuranceError) {

          }
        }
      } catch (err: any) {
        setError(err.message || 'یک خطای ناشناخته رخ داد.');

      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [requestId]);

  const renderImagePreview = (imageUrl: string | undefined, title: string) => {
    if (!imageUrl) return null;
    return (
      <div className="mb-4">
        <h5 className="font-medium text-sm mb-2">{title}</h5>
        <div className="border border-slate-300 rounded-sm p-2 bg-slate-50">
          <img
            src={`${API_CONFIG.uploadURL}${imageUrl}`}
            alt={title}
            className="max-h-40 object-contain mx-auto cursor-pointer"
            onClick={() => {
              setModalImage(`${API_CONFIG.uploadURL}${imageUrl}`);
              setImageModalOpen(true);
            }}
          />
        </div>
      </div>
    );
  };

  if (!requestId) return null;

  return (
    <div>
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p>در حال بارگذاری...</p>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : !requestDetails ? (
        <div className="text-center text-gray-500 py-8">جزئیات درخواست یافت نشد</div>
      ) : (
        <div>
          {/* Status Display */}
          <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700">
              وضعیت فعلی درخواست: {translateStatus(requestDetails.status, requestDetails)}
            </h3>
          </div>

          {/* Instructions Display - Show if instructions exist (regardless of current status) */}
          {requestDetails.instructions && (
            <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
              <h4 className="text-sm font-semibold text-green-700 mb-2">
                <span className="ml-2">📋</span>
                {requestDetails.status === 'approved' ? 'دستورالعمل‌ها:' : 'دستورالعمل‌های قبلی:'}
              </h4>
              <div className="text-green-700 bg-white p-2 rounded border border-green-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.instructions.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-green-600 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Payment Extension Request Display - Show if there was ever a payment extension request */}
          {requestDetails.paymentExtensionStatus && (
            <div className={`mb-6 p-4 rounded-md border ${
              requestDetails.paymentExtensionStatus === 'approved' 
                ? 'bg-green-50 border-green-200' 
                : requestDetails.paymentExtensionStatus === 'rejected'
                ? 'bg-red-50 border-red-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-2 ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700'
                  : 'text-yellow-700'
              }`}>
                <span className="ml-2">
                  {requestDetails.paymentExtensionStatus === 'approved' 
                    ? '✅' 
                    : requestDetails.paymentExtensionStatus === 'rejected'
                    ? '❌'
                    : '⏰'
                  }
                </span>
                درخواست تمدید پرداخت
              </h3>
              <div className={`bg-white p-3 rounded border ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700 border-green-200' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700 border-red-200'
                  : 'text-yellow-700 border-yellow-200'
              }`}>
                {requestDetails.paymentExtensionStatus === 'approved' ? (
                  <p className="font-medium">
                    ✅ درخواست تمدید پرداخت تایید شده است.
                  </p>
                ) : requestDetails.paymentExtensionStatus === 'rejected' ? (
                  <>
                    <p className="mb-2 font-medium">
                      ❌ درخواست تمدید پرداخت رد شده است.
                    </p>
                    {requestDetails.paymentExtensionRejectionReason && (
                      <div className="mt-2 p-2 bg-red-100 rounded border border-red-300">
                        <strong className="text-sm">دلیل رد:</strong>
                        <p className="text-sm mt-1">{requestDetails.paymentExtensionRejectionReason}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="font-medium">
                    ⏳ کاربر درخواست تمدید مهلت پرداخت کرده است.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Rejection Reason Display - Show if rejection reason exists (regardless of current status) */}
          {requestDetails.rejectionReason && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border-red-200">
              <h4 className="text-sm font-semibold text-red-700 mb-2">
                {requestDetails.status === 'rejected' ? 'دلایل رد درخواست:' : 'دلایل رد قبلی:'}
              </h4>
              <div className="text-red-600 bg-white p-2 rounded border border-red-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.rejectionReason.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-red-500 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Amount Display - Show if amount exists (regardless of status) */}
          {requestDetails.amount && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">
                <span className="ml-2">💰</span>
                {requestDetails.status === 'approved' ? 'هزینه قابل پرداخت:' : 'هزینه تعیین شده:'}
              </h4>
              <div className="text-blue-700 bg-white p-2 rounded border border-blue-200 text-sm">
                <div className="text-lg font-bold text-blue-800">
                  {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}
                </div>
              </div>
            </div>
          )}

          {/* User Info Section */}
          {requestDetails.userId && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات کاربر</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.userId.name || ''} ${requestDetails.userId.lastName || ''}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.userId.nationalId || '-'}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.userId.mobile || '-'}</div>
              </div>
            </div>
          )}

          {/* Patient Info Section */}
          {requestDetails.patientInfo && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات بیمار</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.patientInfo.firstName} ${requestDetails.patientInfo.lastName}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.patientInfo.nationalId}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.patientInfo.mobile}</div>
                <div><span className="font-medium text-slate-600">جنسیت:</span> {requestDetails.patientInfo.gender === 'male' ? 'مرد' : 'زن'}</div>
                <div><span className="font-medium text-slate-600">نسبت بیمار:</span> {translatePatientType(requestDetails.patientInfo.patientType)}</div>
                {requestDetails.ageInfo && (
                  <div><span className="font-medium text-slate-600">سن:</span> {requestDetails.ageInfo.age} سال</div>
                )}
              </div>
            </div>
          )}
          
          {/* Sampling Location Section */}
          {requestDetails.samplingLocation && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">محل نمونه گیری</h4>
              <div className="text-sm">
                <p><span className="font-medium text-slate-600">نوع نمونه گیری:</span> 
                  {requestDetails.samplingLocation?.type === 'lab' ? ' نمونه گیری در آزمایشگاه' : ' نمونه گیری در منزل'}
                </p>
                
                {requestDetails.samplingLocation?.type === 'home' && (
                  <>
                    {requestDetails.addressInfo && (
                  <>
                    <p className="mt-2"><span className="font-medium text-slate-600">منطقه:</span> {requestDetails.addressInfo.region}</p>
                    <p>
                      {requestDetails.addressInfo.field && `میدان ${requestDetails.addressInfo.field}، `}
                      {requestDetails.addressInfo.street && `خیابان ${requestDetails.addressInfo.street}، `}
                      {requestDetails.addressInfo.alley && `کوچه ${requestDetails.addressInfo.alley}، `}
                      {requestDetails.addressInfo.building && `ساختمان ${requestDetails.addressInfo.building}، `}
                      {requestDetails.addressInfo.unit && `واحد ${requestDetails.addressInfo.unit}، `}
                      {requestDetails.addressInfo.number && `پلاک ${requestDetails.addressInfo.number}`}
                    </p>
                  </>
                    )}
                    
                    {/* Home Sampling Options */}
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center">
                        <span className="font-medium text-slate-600 ml-2">نیاز به ظرف:</span>
                        {requestDetails.samplingLocation.needsContainer ? (
                          <span className="text-green-600 font-medium">✓ دارد</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ ندارد</span>
                )}
              </div>
                      <div className="flex items-center">
                        <span className="font-medium text-slate-600 ml-2">ایاب و ذهاب:</span>
                        {requestDetails.samplingLocation.freeTransportation ? (
                          <span className="text-green-600 font-medium">✓ رایگان</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ با هزینه</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {requestDetails.samplingLocation?.type === 'lab' && (
                  <p className="mt-2 text-blue-600">
                    <span className="font-medium">📍</span> بیمار باید به آزمایشگاه مراجعه کند
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Insurance Info Section */}
          {requestDetails.insuranceInfo && (
            <div className="mb-8">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">اطلاعات بیمه و آزمایش</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
                <div><span className="font-medium text-slate-600">نوع درخواست:</span> {getInsuranceTypeDisplay(requestDetails.insuranceInfo.type)}</div>
                
                {requestDetails.insuranceInfo.type === 'paper' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                  </>
                )}
                
                {requestDetails.insuranceInfo.type === 'electronic' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                    <div><span className="font-medium text-slate-600">کد رهگیری نسخه:</span> {requestDetails.insuranceInfo.trackingCode}</div>
                  </>
                )}
                
                {requestDetails.insuranceInfo.type === 'package' && selectedPackage && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                      <h5 className="font-medium text-blue-800">{selectedPackage.name}</h5>
                      {selectedPackage.description && (
                        <p className="text-sm text-blue-600 mt-1">{selectedPackage.description}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {requestDetails.insuranceInfo.description && (
                <div className="mt-4">
                  <p className="font-medium text-slate-600 mb-1">توضیحات:</p>
                  <p className="bg-slate-50 p-3 rounded-sm border border-slate-200 text-sm">
                    {requestDetails.insuranceInfo.description}
                  </p>
                </div>
              )}
              
              {/* Uploaded Images */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h5 className="font-bold text-slate-700 mb-4">تصاویر آپلود شده</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {renderImagePreview(requestDetails.insuranceInfo.nationalIdImageUrl, 'تصویر کارت ملی')}
                  {renderImagePreview(requestDetails.insuranceInfo.testListImageUrl, 'لیست آزمایش‌ها')}
                  {renderImagePreview(requestDetails.insuranceInfo.prescriptionImageUrl, 'تصویر نسخه')}
                  {renderImagePreview(requestDetails.insuranceInfo.insuranceBookletImageUrl, 'تصویر دفترچه بیمه')}
                </div>
                {!(requestDetails.insuranceInfo.nationalIdImageUrl || 
                   requestDetails.insuranceInfo.testListImageUrl || 
                   requestDetails.insuranceInfo.prescriptionImageUrl || 
                   requestDetails.insuranceInfo.insuranceBookletImageUrl) && (
                    <p className="text-sm text-slate-500">هیچ تصویری برای این درخواست آپلود نشده است.</p>
                )}
              </div>
            </div>
          )}

          {/* System Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h5 className="font-bold text-slate-700 mb-4">اطلاعات درخواست</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="font-medium text-slate-600">شناسه درخواست:</span> { requestDetails.displayId || requestDetails._id.slice(-8)}</div>
              <div><span className="font-medium text-slate-600">تاریخ ایجاد:</span> {new Intl.DateTimeFormat('fa-IR').format(new Date(requestDetails.createdAt))}</div>
              <div><span className="font-medium text-slate-600">آخرین بروزرسانی:</span> {new Intl.DateTimeFormat('fa-IR').format(new Date(requestDetails.updatedAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پرداخت:</span> {requestDetails.paymentDateTime ? new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.paymentDateTime)) : '-'}</div>
              {requestDetails.samplingDate && (
                <div><span className="font-medium text-slate-600">تاریخ نمونه گیری:</span> {new Intl.DateTimeFormat('fa-IR').format(new Date(requestDetails.samplingDate))}</div>
              )}
              {requestDetails.samplingTime && (
                <div><span className="font-medium text-slate-600">ساعت نمونه گیری:</span> {requestDetails.samplingTime}</div>
              )}
              {requestDetails.amount !== undefined && requestDetails.amount !== null && (
                <div><span className="font-medium text-slate-600">هزینه آزمایش:</span> {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}</div>
              )}
              {(requestDetails.status === 'approved' || requestDetails.status === 'paid') && (
                <div><span className="font-medium text-slate-600">هزینه ایاب و ذهاب:</span> {
                  (() => {
                    if (requestDetails.transportationPrice !== undefined && requestDetails.transportationPrice !== null) {
                      return requestDetails.transportationPrice === 0 
                        ? 'رایگان' 
                        : `${new Intl.NumberFormat('fa-IR').format(requestDetails.transportationPrice)} تومان`;
                    }
                    return requestDetails.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد';
                  })()
                }</div>
              )}
              {(requestDetails.status === 'paid' && requestDetails.amount !== undefined && requestDetails.amount !== null) && (
                <div><span className="font-medium text-slate-600">مجموع پرداختی:</span> {
                  (() => {
                    const amount = requestDetails.amount || 0;
                    const transportation = requestDetails.transportationPrice || 0;
                    const total = amount + transportation;
                    return total === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(total)} تومان`;
                  })()
                }</div>
              )}
            </div>
          </div>

          {/* Transaction History Section */}
          {transactions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تاریخچه تراکنش‌ها</h5>
              {loadingTransactions ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="mt-2 text-sm text-gray-600">در حال بارگذاری تراکنش‌ها...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ردیف</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مبلغ</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">درگاه</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">کد رهگیری</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction, index) => (
                        <tr key={transaction._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {new Intl.NumberFormat('fa-IR').format(transaction.amount)} تومان
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {transaction.transactionType === 'request' ? 'پرداخت اولیه' : 'پرداخت باقیمانده'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${
                              transaction.status === 'completed' ? 'bg-green-100 text-green-800' : 
                              transaction.status === 'failed' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {transaction.status === 'completed' ? 'موفق' : 
                               transaction.status === 'failed' ? 'ناموفق' : 
                               'در انتظار'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {transaction.gateway === 'parsian' ? 'پارسیان' : transaction.gateway}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {transaction.refId || '---'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Intl.DateTimeFormat('fa-IR', { 
                              year: 'numeric', 
                              month: 'numeric', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }).format(new Date(transaction.paymentDate || transaction.createdAt))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Logs */}
          {requestDetails.statusLogs && requestDetails.statusLogs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تاریخچه تغییرات وضعیت</h5>
              <div className="space-y-3">
                {[...requestDetails.statusLogs].reverse().map((log, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            log.changedBy.userType === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {log.changedBy.userType === 'admin' ? '👨‍💼 ادمین' : '👤 کاربر'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{log.changedBy.userName}</span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">اقدام:</span> {log.details || log.action}
                        </div>
                        {log.oldStatus && log.newStatus && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">تغییر وضعیت:</span> {translateStatus(log.oldStatus, requestDetails)} → {translateStatus(log.newStatus, requestDetails)}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-xs text-gray-500">
                          {new Intl.DateTimeFormat('fa-IR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(log.timestamp))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image Modal */}
          {imageModalOpen && modalImage && (
            <div
              className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]"
              onClick={() => setImageModalOpen(false)}
            >
              <div
                className="bg-white p-4 rounded shadow-lg"
                onClick={e => e.stopPropagation()}
              >
                <img src={modalImage} alt="نمایش تصویر" className="max-h-[80vh] max-w-[90vw]" />
                <button
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                  onClick={() => setImageModalOpen(false)}
                >
                  بستن
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fa-IR');
};

// Helper function to get status color and text
const getStatusInfo = (status: string, request?: TestRequest) => {
  switch (status) {
    case 'approved':
      return { color: 'bg-green-100 text-green-800', text: 'تایید شده' };
    case 'canceled':
      return { color: 'bg-gray-100 text-gray-800', text: 'لغو شده' };
    case 'expired':
      return { color: 'bg-orange-100 text-orange-800', text: 'منقضی شده' };
    case 'incomplete':
      return { color: 'bg-purple-100 text-purple-800', text: 'ناتمام' };
    case 'paid':
      // Check if total amount (base + transportation) is 0
      if (request) {
        const baseAmount = request.amount || 0;
        const transportationAmount = request.transportationPrice || 0;
        const totalAmount = baseAmount + transportationAmount;
        
        if (totalAmount === 0) {
          return { color: 'bg-green-100 text-green-800', text: 'نهایی شده' };
        }
      }
      return { color: 'bg-green-100 text-green-800', text: 'پرداخت شده' };
    case 'rejected':
      return { color: 'bg-red-100 text-red-800', text: 'رد شده' };
    default:
      return { color: 'bg-gray-100 text-gray-800', text: status };
  }
};

// Tab components

const PaidTab: React.FC<{ onViewDetails: (requestId: string) => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: TestRequest['addressInfo']) => void }> = ({ onViewDetails, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPaidRequests();
  }, [pagination.currentPage, searchQuery]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (pagination.currentPage !== 1) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [searchQuery]);

  const fetchPaidRequests = async () => {
    try {
      setLoading(true);
      const response = await getRequests({ 
        status: 'paid', 
        search: searchQuery,
        page: pagination.currentPage,
        limit: itemsPerPage
      });
      
      // Debug: Log transaction data to console  

      setRequests(response.data);
      setPagination({
        currentPage: response.pagination.currentPage,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      });
      
      // Notify parent about results
      if (onResultsChange) {
        onResultsChange(response.data.length > 0);
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌های پرداخت شده');
      if (onResultsChange) {
        onResultsChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">در حال بارگذاری...</div>;
  }

  return (
    <div className="mt-4">
      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پرداخت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">هزینه آزمایش</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">هزینه ایاب و ذهاب</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">مجموع هزینه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">کد رهگیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">درگاه پرداخت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={17} className="py-8 text-center text-gray-500">
                  هیچ درخواست پرداخت شده‌ای یافت نشد
                </td>
              </tr>
            ) : (
              requests.map(request => {
                const statusInfo = getStatusInfo(request.status, request);
                return (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.patientInfo?.firstName} {request.patientInfo?.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {(() => {
                        // Show payment date only for completed transactions
                        if (request.transaction?.status === 'completed' && request.transaction?.paymentDate) {
                          return new Intl.DateTimeFormat('fa-IR', { 
                            year: 'numeric', 
                            month: 'numeric', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(request.transaction.paymentDate));
                        } else if (request.paymentDateTime) {
                          return new Intl.DateTimeFormat('fa-IR', { 
                            year: 'numeric', 
                            month: 'numeric', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(request.paymentDateTime));
                        }
                        return '-';
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {request.amount !== undefined && request.amount !== null 
                        ? (request.amount === 0 ? 'رایگان' : `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان`)
                        : '-'
                      }
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {(() => {
                        if (request.transportationPrice !== undefined && request.transportationPrice !== null) {
                          return request.transportationPrice === 0 
                            ? 'رایگان' 
                            : `${new Intl.NumberFormat('fa-IR').format(request.transportationPrice)} تومان`;
                        }
                        return '-';
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">
                      {(() => {
                        const amount = request.amount || 0;
                        const transportation = request.transportationPrice || 0;
                        const total = amount + transportation;
                        return total === 0 ? 'رایگان' : `${new Intl.NumberFormat('fa-IR').format(total)} تومان`;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm font-mono">
                      {request.transaction?.status === 'completed' ? (request.transaction?.refId || '---') : '---'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {(() => {
                        if (request.transaction?.status === 'completed') {
                          const gateway = request.transaction?.gateway;
                          return gateway === 'parsian' ? 'پارسیان' : gateway || '---';
                        }
                        return '---';
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                          onClick={() => onViewDetails(request._id)}
                        >
                          جزئیات
                        </button>
                     
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

const CompletedRequests: React.FC = () => {
  const [activeTab, setActiveTab] = useState('paid');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [tabHasResults, setTabHasResults] = useState<Record<string, boolean>>({ paid: true });
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<TestRequest['addressInfo'] | null>(null);

  // Tab switching handler
  const handleTabChange = (tabId: string) => {
    // Close any open details if switching tabs
    if (selectedRequestId) {
      setSelectedRequestId(null);
    }
    setActiveTab(tabId);
  };

  // Handle search
  const handleSearch = async () => {
    setActiveSearchQuery(searchQuery);
    
    // Check if tab has results
    if (searchQuery.trim()) {
      await checkTabForResults(searchQuery);
    }
  };

  // Check if paid tab has results
  const checkTabForResults = async (search: string) => {
    try {
      const response = await getRequests({
        status: 'paid',
        page: 1,
        limit: 1,
        search
      });
      setTabHasResults({ paid: response.data.length > 0 });
    } catch (error) {

      setTabHasResults({ paid: true }); // Show tab if there's an error
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setTabHasResults({ paid: true }); // Reset all tabs to visible
  };

  // Handle search on Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Details handlers
  const handleViewDetails = (requestId: string) => {
    setSelectedRequestId(requestId);
    // Scroll to top when details modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseDetails = () => {
    setSelectedRequestId(null);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">درخواست‌های نهایی و پرداخت شده</h1>

        {/* Inline Request Details (shown when a request is selected) - Full Screen Mode */}
        {selectedRequestId ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">جزئیات درخواست</h2>
              <button
                onClick={handleCloseDetails}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
              >
                ← برگشت به لیست
              </button>
            </div>
            <RequestDetails
              requestId={selectedRequestId}
              onClose={handleCloseDetails}
            />
          </div>
        ) : (
          <>
            {/* Search Field */}
            <div className="mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="جستجو بر اساس نام، نام خانوادگی، کد ملی، موبایل یا شناسه..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  جستجو
                </button>
                {activeSearchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    پاک کردن
                  </button>
                )}
              </div>
            </div>
            
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px space-x-8">
                {(!activeSearchQuery || tabHasResults.paid) && (
                  <button
                    onClick={() => handleTabChange('paid')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'paid'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    درخواست‌های نهایی و پرداخت شده
                  </button>
                )}
              </nav>
            </div>
            
            {activeTab === 'paid' && <PaidTab onViewDetails={handleViewDetails} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, paid: hasResults }))} onAddressClick={(addressInfo) => {
              setSelectedAddress(addressInfo);
              setAddressModalOpen(true);
            }} />}
          </>
        )}
      </div>

      {/* Address Modal */}
      {addressModalOpen && selectedAddress && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setAddressModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">آدرس کامل</h3>
              <button
                onClick={() => setAddressModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 text-right">
              {selectedAddress.region && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">منطقه:</span>
                  <span className="text-gray-600">{selectedAddress.region}</span>
                </div>
              )}
              {selectedAddress.field && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">میدان:</span>
                  <span className="text-gray-600">{selectedAddress.field}</span>
                </div>
              )}
              {selectedAddress.street && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">خیابان:</span>
                  <span className="text-gray-600">{selectedAddress.street}</span>
                </div>
              )}
              {selectedAddress.alley && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">کوچه:</span>
                  <span className="text-gray-600">{selectedAddress.alley}</span>
                </div>
              )}
              {selectedAddress.building && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">ساختمان:</span>
                  <span className="text-gray-600">{selectedAddress.building}</span>
                </div>
              )}
              {selectedAddress.unit && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">واحد:</span>
                  <span className="text-gray-600">{selectedAddress.unit}</span>
                </div>
              )}
              {selectedAddress.number && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">پلاک:</span>
                  <span className="text-gray-600">{selectedAddress.number}</span>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="font-medium text-gray-700 mb-2">آدرس کامل:</div>
                <div className="text-gray-600 leading-relaxed">
                  {[
                    selectedAddress.region && `منطقه ${selectedAddress.region}`,
                    selectedAddress.field && `میدان ${selectedAddress.field}`,
                    selectedAddress.street && `خیابان ${selectedAddress.street}`,
                    selectedAddress.alley && `کوچه ${selectedAddress.alley}`,
                    selectedAddress.building && `ساختمان ${selectedAddress.building}`,
                    selectedAddress.unit && `واحد ${selectedAddress.unit}`,
                    selectedAddress.number && `پلاک ${selectedAddress.number}`
                  ].filter(Boolean).join('، ')}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setAddressModalOpen(false)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedRequests;
